import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import { movies, typeLabels } from '@/mocks/movies';
import { searchMovies } from '@/utils/movie';

const hotKeywords = ['夜行列车', '悬疑', '综艺', '家庭', '沈砚舟', '动画', '2026'];

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get('q') ?? '';
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchMovies(keyword), [keyword]);

  const suggestions = useMemo(
    () => [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    [],
  );

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = input.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <SiteLayout
      title={keyword ? `搜索「${keyword}」 - 丝瓜视频` : '影视搜索 - 丝瓜视频'}
      description={
        keyword
          ? `丝瓜视频搜索「${keyword}」的结果，共 ${results.length} 部相关影视作品，支持电影、电视剧与综艺名称模糊搜索。`
          : '在丝瓜视频搜索电影、电视剧与综艺，支持作品名称、导演、演员与标签的模糊搜索。'
      }
      keywords="丝瓜视频搜索,影视搜索,找片,影视资源检索"
    >
      <main className="flex-1 w-full pb-10">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500 mb-3">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>搜索</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              影视搜索
            </h1>
            <p className="mt-1.5 text-xs md:text-sm text-foreground-600">
              支持作品名称、导演、演员与内容标签的模糊匹配
            </p>

            <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="输入影视名称、导演或演员"
                  aria-label="搜索关键词"
                  className="w-full h-11 pl-10 pr-10 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                />
                {input && (
                  <button
                    type="button"
                    onClick={() => setInput('')}
                    aria-label="清空输入"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-close-circle-fill text-base"></i>
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-500">热门搜索：</span>
              {hotKeywords.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-6 md:mt-8">
          {keyword ? (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <p className="text-sm text-foreground-700">
                  搜索词：
                  <span className="font-bold text-foreground-950">「{keyword}」</span>
                </p>
                <Link
                  to="/search"
                  className="px-2.5 py-1 rounded-md bg-background-100 border border-background-200 text-xs text-foreground-600 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-close-line"></i> 清空搜索
                </Link>
                <span className="text-xs text-foreground-500 ml-auto">
                  找到 <span className="font-bold text-primary-600 font-label">{results.length}</span> 个结果
                </span>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  <EmptyState
                    title="没有找到相关作品"
                    description={`「${keyword}」暂时没有匹配的影视内容。可以换一个更短的关键词，或者先从下面这些热门作品开始浏览。`}
                    actionLabel="清空搜索词"
                    onAction={() => navigate('/search')}
                    secondaryLabel="返回首页"
                    secondaryTo="/"
                  />
                  <div>
                    <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-fire-line text-primary-500"></i> 大家都在看
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                      {suggestions.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i> 当前热门
                </h2>
                <div className="space-y-3">
                  {suggestions.map((movie, i) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="flex items-center gap-3 group"
                    >
                      <span className="w-6 font-label text-sm font-bold text-foreground-300 text-center">
                        {i + 1}
                      </span>
                      <div className="w-11 aspect-[5/7] rounded overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={movie.poster}
                          alt={movie.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                          {movie.title}
                        </p>
                        <p className="text-[11px] text-foreground-500">
                          {typeLabels[movie.type]} · {movie.year} · {movie.genres.slice(0, 2).join('/')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                  按分类浏览
                </h2>
                <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                  还不知道看什么？可以先从分类频道进入，再用地区、年份与评分筛选出适合当下心情的作品。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(['movie', 'tv', 'variety'] as const).map((type) => (
                    <Link
                      key={type}
                      to={`/category/${type}`}
                      className="rounded-md bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors"
                    >
                      <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center mb-2">
                        <i
                          className={`${
                            type === 'movie'
                              ? 'ri-film-line'
                              : type === 'tv'
                                ? 'ri-tv-2-line'
                                : 'ri-mic-line'
                          } text-base`}
                        ></i>
                      </span>
                      <p className="text-sm font-bold text-foreground-950">{typeLabels[type]}</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">
                        {type === 'movie' ? '长片佳作' : type === 'tv' ? '剧集追更' : '轻松节目'}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </SiteLayout>
  );
}